import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import logger from "../utils/logger";

function captureFrameFromVideo(videoElement) {
  if (!videoElement) return null;

  const canvas = document.createElement('canvas');
  canvas.width = videoElement.videoWidth;
  canvas.height = videoElement.videoHeight;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL('image/jpeg', 0.95);
}

const STABILIZATION_CONFIG = {
  requiredFrames: 6,
  maxLandmarkMovement: 0.010,
  keyLandmarks: [1, 33, 263, 133, 362, 10, 152],
};

let actionHistory = [];
let bestStableFrame = null;
let previousLandmarks = null;

export function resetStabilization() {
  actionHistory = [];
  bestStableFrame = null;
  previousLandmarks = null;
}

function getActiveAction(faceAction) {
  if (faceAction.right) return 'right';
  if (faceAction.left) return 'left';
  if (faceAction.up) return 'up';
  if (faceAction.down) return 'down';
  if (faceAction.smile) return 'smile';
  if (faceAction.rightblink) return 'rightblink';
  if (faceAction.leftblink) return 'leftblink';
  return null;
}

function getActionScore(action, categories, landmarks) {
  if (!action) return 0;

  const score = (name) => {
    const category = categories.find((c) => c.categoryName === name);
    return category ? category.score : 0;
  };

  switch (action) {
    case 'smile':
      return (score('mouthSmileLeft') + score('mouthSmileRight')) / 2;
    case 'rightblink':
      return score('eyeBlinkRight');
    case 'leftblink':
      return score('eyeBlinkLeft');
    case 'up':
      return (score('eyeLookDownLeft') + score('eyeLookDownRight')) / 2;
    case 'down':
      return (score('eyeLookUpLeft') + score('eyeLookUpRight')) / 2;
    case 'right':
    case 'left': {
      const noseTip = landmarks[1];
      const xCenter = (landmarks[133].x + landmarks[362].x) / 2;
      return Math.abs(noseTip.x - xCenter);
    }
    default:
      return 0;
  }
}

function calculateLandmarkMovement(currentLandmarks, prevLandmarks) {
  if (!prevLandmarks) return 0;

  let totalMovement = 0;
  for (const idx of STABILIZATION_CONFIG.keyLandmarks) {
    const curr = currentLandmarks[idx];
    const prev = prevLandmarks[idx];
    if (curr && prev) {
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      totalMovement += Math.sqrt(dx * dx + dy * dy);
    }
  }
  return totalMovement / STABILIZATION_CONFIG.keyLandmarks.length;
}

function checkStabilization(currentAction, landmarks, getFrame, score, expectedAction) {
  const movement = calculateLandmarkMovement(landmarks, previousLandmarks);
  previousLandmarks = landmarks.map(l => ({ x: l.x, y: l.y, z: l.z }));

  const isStable = movement < STABILIZATION_CONFIG.maxLandmarkMovement;

  if (currentAction && isStable) {
    actionHistory.push(currentAction);
    if (!bestStableFrame || score > bestStableFrame.score) {
      bestStableFrame = { image: getFrame(), score, action: currentAction };
      logger.log("[liveness] new best frame:", {
        action: currentAction,
        score: Number(score.toFixed(4)),
      });
    }
  } else {
    actionHistory = currentAction ? [currentAction] : [];
    bestStableFrame = null;
  }

  if (actionHistory.length > STABILIZATION_CONFIG.requiredFrames) {
    actionHistory = actionHistory.slice(-STABILIZATION_CONFIG.requiredFrames);
  }

  const allSameAction = actionHistory.length >= STABILIZATION_CONFIG.requiredFrames &&
    actionHistory.every(a => a === currentAction);

  const isAppropriateForInstruction =
    !expectedAction || (bestStableFrame && bestStableFrame.action === expectedAction);

  const isStabilized = allSameAction && isStable && isAppropriateForInstruction;

  if (isStabilized && bestStableFrame) {
    logger.log("[liveness] stabilized frame selected:", {
      action: bestStableFrame.action,
      score: Number(bestStableFrame.score.toFixed(4)),
      expectedAction: expectedAction ?? "(none)",
      frameCount: actionHistory.length,
      movement: Number(movement.toFixed(5)),
    });
  }

  return {
    isStabilized,
    frameCount: actionHistory.length,
    movement,
    isStable,
    bestFrame: isStabilized ? bestStableFrame : null,
  };
}

function validateFaceQuality(landmarks) {
  const leftCheek = landmarks[234];
  const rightCheek = landmarks[454];
  const chin = landmarks[152];
  const forehead = landmarks[10];
  const leftEyeOuter = landmarks[33];
  const rightEyeOuter = landmarks[263];

  const faceWidth = Math.abs(rightCheek.x - leftCheek.x);
  const faceHeight = Math.abs(chin.y - forehead.y);

  if (faceWidth < 0.15 || faceHeight < 0.20) {
    return { valid: false, reason: 'face_too_small' };
  }

  const allLandmarksVisible =
    leftCheek.x > 0.05 && rightCheek.x < 0.95 &&
    forehead.y > 0.02 && chin.y < 0.98;

  if (!allLandmarksVisible) {
    return { valid: false, reason: 'face_partially_visible' };
  }

  const eyeDistance = Math.abs(rightEyeOuter.x - leftEyeOuter.x);

  if (eyeDistance < 0.06) {
    return { valid: false, reason: 'face_too_rotated' };
  }

  return { valid: true, reason: null };
}

function classifyFaceAction(result, threshold = 0.75) {
  if (!result || !result['faceBlendshapes'] || result['faceBlendshapes'].length === 0) {
    return null;
  }

  const categories = result['faceBlendshapes'][0].categories;
  const faceAction = {
    right: false,
    left: false,
    up: false,
    down: false,
    smile: false,
    rightblink: false,
    leftblink: false,
    capturedImage: null,
    faceQuality: null,
    stabilization: null,
  };
  const getCategoryScore = (categoryName) => {
    const category = categories.find(c => c.categoryName === categoryName);
    return category ? category.score : 0;
  };

  if (!result.faceLandmarks || result.faceLandmarks.length === 0) {
    faceAction.faceQuality = { valid: false, reason: 'no_landmarks' };
    return { faceAction, currentAction: null, categories, landmarks: null };
  }

  const landmarks = result.faceLandmarks[0];
  const qualityCheck = validateFaceQuality(landmarks);
  faceAction.faceQuality = qualityCheck;

  if (!qualityCheck.valid) {
    return { faceAction, currentAction: null, categories, landmarks };
  }

  faceAction.down = getCategoryScore('eyeLookUpLeft') > 0.6 && getCategoryScore('eyeLookUpRight') > 0.6;
  faceAction.up = getCategoryScore('eyeLookDownLeft') > 0.6 && getCategoryScore('eyeLookDownRight') > 0.6;
  faceAction.smile = getCategoryScore('mouthSmileLeft') > threshold && getCategoryScore('mouthSmileRight') > threshold;
  faceAction.rightblink = getCategoryScore('eyeBlinkRight') > 0.5 && getCategoryScore('eyeBlinkLeft') < 0.4;
  faceAction.leftblink = getCategoryScore('eyeBlinkLeft') > 0.5 && getCategoryScore('eyeBlinkRight') < 0.4;

  const noseTip = landmarks[1];
  const leftEyeInnerCorner = landmarks[133];
  const rightEyeInnerCorner = landmarks[362];
  const forehead = landmarks[10];

  const xCenter = (leftEyeInnerCorner.x + rightEyeInnerCorner.x) / 2;
  const xDiff = noseTip.x - xCenter;
  const yForeheadDiff = forehead.y - noseTip.y;

  if (xDiff < -0.04) {
    faceAction.right = true;
  } else if (xDiff > 0.04) {
    faceAction.left = true;
  } else if (yForeheadDiff < -0.02) {
    // Looking Up
  } else if (yForeheadDiff > 0.02) {
    // Looking Down
  }

  const currentAction = getActiveAction(faceAction);
  return { faceAction, currentAction, categories, landmarks };
}

export async function getAction(result, videoElement, expectedAction = null, threshold = 0.75) {
  const classified = classifyFaceAction(result, threshold);
  if (!classified) {
    return null;
  }

  const { faceAction, currentAction, categories, landmarks } = classified;
  if (!faceAction.faceQuality?.valid || !landmarks) {
    return faceAction;
  }

  const actionScore = getActionScore(currentAction, categories, landmarks);
  const stabilization = checkStabilization(
    currentAction,
    landmarks,
    () => captureFrameFromVideo(videoElement),
    actionScore,
    expectedAction
  );
  faceAction.stabilization = stabilization;

  if (stabilization.isStabilized && stabilization.bestFrame) {
    faceAction.capturedImage = stabilization.bestFrame.image;
  }

  return faceAction;
}

let imageLandmarker = null;
let imageLandmarkerPromise = null;

async function getImageLandmarker() {
  if (imageLandmarker) return imageLandmarker;
  if (!imageLandmarkerPromise) {
    imageLandmarkerPromise = (async () => {
      const resolver = await FilesetResolver.forVisionTasks("/mediapipe/tasks-vision/wasm");
      imageLandmarker = await FaceLandmarker.createFromOptions(resolver, {
        baseOptions: {
          modelAssetPath: "/model/face_landmarker.task",
          delegate: "CPU",
        },
        outputFaceBlendshapes: true,
        runningMode: "IMAGE",
        numFaces: 1,
      });
      return imageLandmarker;
    })();
  }
  return imageLandmarkerPromise;
}

export function warmUpImageLandmarker() {
  getImageLandmarker().catch(() => { /* ignore — retried during verify */ });
}

export function disposeImageLandmarker() {
  if (imageLandmarker && typeof imageLandmarker.close === 'function') {
    try {
      imageLandmarker.close();
    } catch (e) {
    }
  }
  imageLandmarker = null;
  imageLandmarkerPromise = null;
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function verifyActionOnImage(imageSrc, expectedAction, threshold = 0.75) {
  if (!imageSrc || !expectedAction) return false;

  try {
    const landmarker = await getImageLandmarker();
    const img = await loadImageElement(imageSrc);
    const result = landmarker.detect(img);

    const classified = classifyFaceAction(result, threshold);
    if (!classified) {
      logger.log("[liveness] static verification: face/blendshape not found", { expectedAction });
      return false;
    }

    const { faceAction, categories, landmarks } = classified;

    const activeActions = ['right', 'left', 'up', 'down', 'smile', 'rightblink', 'leftblink']
      .filter((action) => faceAction[action]);
    const verified =
      Boolean(faceAction.faceQuality?.valid) &&
      activeActions.length === 1 &&
      activeActions[0] === expectedAction;

    const score = (name) => {
      const category = (categories || []).find((c) => c.categoryName === name);
      return category ? Number(category.score.toFixed(3)) : 0;
    };
    let headTurnXDiff = null;
    if (landmarks) {
      const noseTip = landmarks[1];
      const xCenter = (landmarks[133].x + landmarks[362].x) / 2;
      headTurnXDiff = Number((noseTip.x - xCenter).toFixed(4));
    }

    logger.log("[liveness] static frame verification:", {
      expectedAction,
      verified,
      activeActions,
      faceQuality: faceAction.faceQuality,
      headTurnXDiff,
      scores: {
        mouthSmileLeft: score('mouthSmileLeft'),
        mouthSmileRight: score('mouthSmileRight'),
        eyeBlinkLeft: score('eyeBlinkLeft'),
        eyeBlinkRight: score('eyeBlinkRight'),
        eyeLookUpLeft: score('eyeLookUpLeft'),
        eyeLookUpRight: score('eyeLookUpRight'),
        eyeLookDownLeft: score('eyeLookDownLeft'),
        eyeLookDownRight: score('eyeLookDownRight'),
      },
    });

    return verified;
  } catch (error) {
    logger.error("[liveness] static verification error:", error);
    return false;
  }
}
